import { Room, RoomRuleTypeIds } from '@spike/room-model';
import { RoomsStatus, saveThunk } from '@spike/rooms-action';
import useNonInitialEffect from '@versiondos/hooks';
import { OverFullWindow } from 'components/UI';
import { MultiStepForm } from 'components/UI/V3/Forms/MultiStepForm/MultiStepForm';
import ExitWithoutSavingModal from 'components/UI/V3/Modals/ExitWithoutSavingModal';
import { useApiClientWrapper } from 'hooks';
import isEqual from 'lodash/isEqual';
import { FunctionComponent, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { v4 as uuid } from 'uuid';
import styles from './EditRoom.module.scss';
import RoomDetails from './RoomDetails';
import Rules from './Rules';
import Success from './Success';
import Units from './Units';
import { GenericDeleteModal } from 'components/UI/V3/Modals/DeleteModal';
import { AppointmentsListStatus, fetchThunk } from '@spike/appointments-list-action';
import { ListType } from 'components/Bookings2/List/AppointmentsList';
import moment from 'moment-timezone';
import { AppointmentListItem } from '@spike/appointments-list-model';

interface EditRoomProps {
    room?: Room;
    onClose?: () => void;
    onSaved?: () => void;
}

export const EditRoom: FunctionComponent<EditRoomProps> = props => {
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();
    const [loading, setLoading] = useState(false);

    const list = useSelector<RootState, Array<AppointmentListItem> | null>(state => {
        switch (props.room?.roomType?.name?.toLowerCase()) {
            case 'daycare':
                return state.appointmentsList.daycares.list;
            case 'boarding':
                return state.appointmentsList.boardings.list;
            default:
                return [];
        }
    });

    const appointmentsListStatus = useSelector<RootState, AppointmentsListStatus>(
        state => state.appointmentsList.status
    );

    const roomsStatus = useSelector<RootState, RoomsStatus>(state => state.rooms.status);

    const [saving, setSaving] = useState(false);
    const [nameError, setNameError] = useState<string | null>(null);

    const [show, setShow] = useState({ unsavedModal: false, success: false, deactivateModal: false });
    const isEdit = props.room !== undefined;

    useNonInitialEffect(() => {
        switch (roomsStatus) {
            case RoomsStatus.SaveSuccess:
                setSaving(false);
                setShow(prev => ({ ...prev, success: true }));
                break;
            case RoomsStatus.DuplicatedName:
                setSaving(false);
                setNameError('This room name already exists');
                setStep(0);
                break;
            case RoomsStatus.Error:
                setSaving(false);
                break;
        }
    }, [roomsStatus]);

    useNonInitialEffect(() => {
        switch (appointmentsListStatus) {
            case AppointmentsListStatus.DaycaresFetching:
            case AppointmentsListStatus.BoardingsFetching:
                setLoading(true);
                break;
            case AppointmentsListStatus.DaycaresFetchSuccess:
            case AppointmentsListStatus.BoardingsFetchSuccess:
                if (list && list.length > 0) {
                    setShow(prev => ({ ...prev, deactivateModal: true }));
                } else {
                    setStep(2);
                }
                setLoading(false);
                break;
            case AppointmentsListStatus.DaycaresError:
            case AppointmentsListStatus.BoardingsError:
                setLoading(false);
                setStep(2);
                break;
        }
    }, [appointmentsListStatus]);

    const title = props.room ? 'Editing Room or Space' : 'Add Room or Space';

    const [step, setStep] = useState(0);

    const newRoom: Room = {
        uuid: uuid(),
        roomType: null,
        name: '',
        description: '',
        photos: [],
        petTypes: [],
        restrictions: {
            breeds: {
                dogBreedIds: [],
                catBreedIds: []
            },
            petTagIds: []
        },
        units: [{ uuid: uuid(), name: 'Room 1', active: true }],
        staffIds: [],
        rules: []
    };

    const [editRoom, setEditRoom] = useState<Room>(props.room ?? newRoom);

    const saveHandler = () => {
        setNameError(null);
        setSaving(true);
        dispatch(saveThunk(apiClientWrapper, editRoom));
    };

    const closeHandler = () => {
        if (show.success) {
            props.onSaved?.();
        } else if (!isEqual(props.room ?? newRoom, editRoom)) {
            setShow(prev => ({ ...prev, unsavedModal: true }));
        } else {
            props.onClose?.();
        }
    };

    const handleNext = () => {
        if (props.room) {
            const inactiveUnitIds = props.room.units
                .filter(originalUnit => {
                    const currentUnit = editRoom.units.find(unit => unit.uuid === originalUnit.uuid);
                    return originalUnit.active === true && currentUnit?.active === false && originalUnit.id != null;
                })
                .map(unit => unit.id!);

            if (inactiveUnitIds.length > 0) {
                dispatch(
                    fetchThunk(
                        apiClientWrapper,
                        editRoom?.roomType?.name?.toLowerCase() as ListType,
                        editRoom?.roomType?.name.toLowerCase() as ListType,
                        [],
                        `${editRoom?.roomType?.name.toLowerCase()}_all`,
                        moment().format('YYYY/MM/DD'),
                        null,
                        null,
                        [1, 2, 3, 4, 5, 6],
                        inactiveUnitIds,
                        ['all'],
                        [],
                        null,
                        null
                    )
                );
            } else {
                setStep(2);
            }
        } else {
            setStep(2);
        }
    };

    const handleCancelDeactivate = () => {
        if (props.room) {
            setEditRoom(prev => ({
                ...prev,
                units: props.room!.units
            }));
        }
        setShow(prev => ({ ...prev, deactivateModal: false }));
    };

    const handleConfirmDeactivate = () => {
        setShow(prev => ({ ...prev, deactivateModal: false }));
        setStep(2);
    };

    return (
        <OverFullWindow
            title={title}
            showMobileLogo={true}
            onClose={closeHandler}
        >
            {!show.success && (
                <div className={styles.container}>
                    <MultiStepForm
                        steps={[
                            {
                                title: 'Room Details',
                                component: (
                                    <RoomDetails
                                        room={editRoom}
                                        nameError={nameError}
                                        onChange={setEditRoom}
                                        onNext={() => setStep(1)}
                                    />
                                )
                            },
                            {
                                title: 'Units',
                                description: 'Set the number of units for this room or space, and name each one.',
                                component: (
                                    <Units
                                        room={editRoom}
                                        isEdit={isEdit}
                                        loading={loading}
                                        onChange={setEditRoom}
                                        onNext={handleNext}
                                    />
                                )
                            },
                            {
                                title: 'Rules',
                                description: 'Select a species for this restriction or capacity.',
                                component: (
                                    <Rules
                                        room={editRoom}
                                        onChange={setEditRoom}
                                        onSave={saveHandler}
                                        saving={saving}
                                    />
                                )
                            }
                        ]}
                        activeStep={step}
                        onEdit={setStep}
                        className={styles.multipleStepForm}
                    />
                </div>
            )}
            {show.unsavedModal && (
                <ExitWithoutSavingModal
                    onDiscard={() => props.onClose?.()}
                    onKeepEditing={() => setShow(prev => ({ ...prev, unsavedModal: false }))}
                />
            )}
            {show.success && (
                <Success
                    roomName={editRoom.name}
                    updated={props.room !== undefined}
                    onClick={props.onSaved}
                />
            )}
            {show.deactivateModal && (
                <GenericDeleteModal
                    title={<span className={styles.title}>Mark units as unavailable?</span>}
                    body={
                        <span className={styles.body}>
                            Existing and future bookings assigned to this unit will be unassigned.
                        </span>
                    }
                    onConfirm={handleConfirmDeactivate}
                    onCancel={handleCancelDeactivate}
                />
            )}
        </OverFullWindow>
    );
};

export default EditRoom;
